// VisiGiftInsights.jsx
import React, { useState, useMemo } from "react";

// Curated stakeholder groups for VisiGift
const STAKEHOLDERS = [
  {
    id: "user",
    label: "User",
    emoji: "👤",
    pains: [
      "Limited time for engaging content without clear rewards.",
      "Irrelevant ads wasting time and leading to low engagement.",
      "Uncertainty about the real value and ease of redeeming earned points.",
      "Difficulty tracking daily earnings and progress across devices.",
      "Concerns over data privacy and ad overload in daily routines."
    ],
    opportunities: [
      "Earn points instantly by landing on home and clicking buttons for quick rewards.",
      "Personalized ads based on interests, ensuring relevant and rewarding interactions.",
      "Seamless redemption of points (e.g., 100 points = ₹10 off) on Think5Whys shopping platform.",
      "Real-time dashboard for viewing points, streaks, and redemption history.",
      "Secure, low-data platform with voice guidance for effortless participation."
    ]
  },
  {
    id: "advertisers",
    label: "Advertisers",
    emoji: "📢",
    pains: [
      "High ad spend with low engagement and conversion rates.",
      "Challenges in targeting niche audiences effectively.",
      "Lack of transparent analytics to measure true ROI.",
      "Difficulty standing out in crowded digital ad spaces.",
      "Slow feedback loops on campaign performance."
    ],
    opportunities: [
      "Point-incentivized ads boost completion rates and user interactions.",
      "Precise targeting by demographics, location, and interests for optimal reach.",
      "Comprehensive analytics dashboard tracking views, clicks, and redemptions.",
      "Custom formats like videos, buttons, and interactive exhibits for standout campaigns.",
      "Real-time optimization tools to adjust budgets and content for maximum impact."
    ]
  },
  {
    id: "influencer",
    label: "Influencer",
    emoji: "🌟",
    pains: [
      "Inconsistent compensation for promotional efforts.",
      "Low audience engagement with non-rewarding sponsored content.",
      "Challenges integrating ads seamlessly into authentic content.",
      "Limited tools to track referral-driven earnings.",
      "Difficulty scaling collaborations without robust platforms."
    ],
    opportunities: [
      "Competitive commissions plus bonus points from user redemptions.",
      "Reward-based shares encourage follower participation and virality.",
      "Easy integration tools for embedding ads in posts and stories.",
      "Dedicated referral tracking with analytics on engagement and conversions.",
      "Grow influence through VisiGift's ecosystem, unlocking more sponsored deals."
    ]
  },
  {
    id: "exhibitors",
    label: "Exhibitors",
    emoji: "🏪",
    pains: [
      "High costs and complexity in setting up digital showcases.",
      "Low visibility for products in competitive online spaces.",
      "Fragmented lead generation without direct sales paths.",
      "Challenges in managing virtual exhibits and inventory digitally.",
      "Slow conversion from views to actual purchases."
    ],
    opportunities: [
      "Free basic listings with pay-per-engagement for premium features.",
      "Point-driven traffic from user interactions to boost product visibility.",
      "Integrated funnels to Think5Whys cart for instant redemptions and sales.",
      "Drag-and-drop tools for video showcases and interactive buttons.",
      "Geo-targeted exhibits connecting local buyers for faster conversions."
    ]
  },
  {
    id: "exhibitorsLead",
    label: "Exhibitors Lead",
    emoji: "📞",
    pains: [
      "Receiving unqualified or irrelevant leads from ad campaigns.",
      "Overwhelm from high-volume inquiries without management tools.",
      "Low close rates due to mismatched buyer intent.",
      "Difficulty integrating leads with existing sales pipelines.",
      "Lack of insights into lead quality and follow-up effectiveness."
    ],
    opportunities: [
      "Qualified leads from engaged, point-earning users with purchase intent.",
      "Built-in CRM for auto-categorization, scheduling, and notifications.",
      "Higher close rates via pre-warmed interactions and exclusive redemption deals.",
      "Seamless export to external CRMs or local systems for streamlined workflows.",
      "Analytics on lead demographics, conversion paths, and point values for optimization."
    ]
  },
  {
    id: "localAdBoutiqueOwner",
    label: "Local Ad Boutique Owner",
    emoji: "🏢",
    pains: [
      "Prohibitive costs for digital advertising in small budgets.",
      "Low digital adoption in local communities leading to poor reach.",
      "Challenges creating professional ads without tech expertise.",
      "Fragmented tracking of local footfall and sales from online efforts.",
      "Limited partnerships for community-focused promotions."
    ],
    opportunities: [
      "Micro-campaigns starting at ₹100/day with scalable point rewards.",
      "Voice-guided, low-data ads accessible to all local users.",
      "User-friendly ad builder with templates for photos, videos, and geo-fencing.",
      "Trackable paths from ad clicks to in-store visits and Think5Whys redemptions.",
      "Collaborations with influencers and exhibitors for amplified local impact."
    ]
  }
];

// Key USPs for VisiGift
const USPs = [
  {
    id: "effortlessEarning",
    title: "Effortless Point Earning",
    desc: "Earn points simply by landing on the home page, clicking buttons, and engaging with short ads or videos – no complex tasks required.",
    emoji: "🎁",
  },
  {
    id: "personalizedAds",
    title: "Personalized Ad Experiences",
    desc: "Tailored ads based on your interests and location, ensuring relevant content that feels rewarding rather than intrusive.",
    emoji: "🎯",
  },
  {
    id: "seamlessRedemption",
    title: "Seamless Redemption",
    desc: "Redeem points directly for discounts on Think5Whys platform (e.g., gadgets, books, essentials) with one-tap checkout integration.",
    emoji: "🛒",
  },
  {
    id: "realTimeAnalytics",
    title: "Real-Time Analytics",
    desc: "Dashboards for users, advertisers, and owners to track points, engagements, conversions, and ROI in live updates.",
    emoji: "📈",
  },
  {
    id: "influencerTools",
    title: "Influencer Collaboration",
    desc: "Tools for seamless ad endorsements, referral tracking, and bonus commissions to grow your influence and earnings.",
    emoji: "🔗",
  },
  {
    id: "localBoost",
    title: "Local Business Boost",
    desc: "Geo-targeted campaigns and point incentives drive footfall and online sales for boutiques and exhibitors.",
    emoji: "📍",
  },
  {
    id: "securePlatform",
    title: "Secure & Trusted",
    desc: "ISO-certified privacy, low-data access, and transparent rewards build trust for all stakeholders.",
    emoji: "🔒",
  },
];

const Pill = ({ children }) => (
  <span className="inline-block bg-purple-700/30 text-purple-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

export default function VisiGiftInsights() {
  const [tab, setTab] = useState("stakeholders"); 
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filteredStakeholders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-purple-950 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="mx-auto mb-2 text-center">
        <h1 className="text-lg md:text-xl font-bold text-purple-400">VisiGift Insights</h1>
        <p className="text-gray-300 mt-2 text-sm md:text-base">
          Rewarding ads module: Earn points through engagement and redeem on Think5Whys for smart shopping.
        </p>
      </header>

      {/* Tabs */}
      <nav className="grid grid-cols-2 md:grid-cols-2 gap-2 justify-center mb-2 place-items-center">
        <button
          onClick={() => setTab("stakeholders")}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "stakeholders"
              ? "bg-purple-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          👤 Stakeholders
        </button>

        <button
          onClick={() => setTab("usps")}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "usps"
              ? "bg-purple-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          🎁 USPs
        </button>
      </nav>

      {/* Search */}
      <div className="flex flex-col md:flex-row justify-center gap-2 mb-2 place-items-center">
        <input
          aria-label="Search stakeholders"
          placeholder="Search stakeholders, pain or opportunity..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          onClick={() => { setQuery(""); setExpandedId(null); }}
          className="text-sm mt-4 w-20 bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
        >
          Reset
        </button>
      </div>

      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders */}
        {tab === "stakeholders" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStakeholders.map((s) => {
              const isOpen = expandedId === s.id;
              return (
                <article key={s.id} className="bg-gradient-to-br from-gray-800 to-purple-900/30 rounded-lg border border-purple-800/30 shadow-sm overflow-hidden">
                  <div className="p-4 flex items-start gap-3">
                    <div className="text-3xl">{s.emoji}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-purple-300">{s.label}</h3>
                        <button
                          onClick={() => setExpandedId(isOpen ? null : s.id)}
                          className="ml-2 bg-purple-700/30 text-sm px-2 py-1 rounded"
                        >
                          {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                        </button>
                      </div>
                      <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                        <strong>Pain points:</strong> {s.pains[0]} • {s.pains[1]} • {s.pains[2]}
                      </p>
                      {isOpen && (
                        <div className="mt-3 space-y-3 text-sm text-gray-200">
                          <div>
                            <h4 className="font-medium text-purple-300">Pain Points</h4>
                            <ol className="list-decimal list-inside mt-1 space-y-1">
                              {s.pains.map((p, idx) => <li key={idx}>{p}</li>)}
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-medium text-purple-300">Opportunities</h4>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              {s.opportunities.map((o, idx) => <li key={idx}>{o}</li>)}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {filteredStakeholders.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results found for your search.</div>
            )}
          </section>
        )}

        {/* USPs */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-purple-700/20 to-gray-800 rounded-lg p-4 border border-purple-600/20">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-purple-300">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        VisiGift • Earn Points from Ads, Redeem on Think5Whys – Empowering Smart Engagement 🌟
      </footer>
    </div>
  );
}